import { useState } from "react"
import { useNavigate } from "react-router-dom"
import {
  IconStar,
  IconStarFilled,
  IconPlus,
  IconPencil,
  IconTrash,
  IconDots,
  IconLayoutDashboard,
} from "@tabler/icons-react"
import { Button } from "@/components/ui/button"
import { DataTable, type DataTableColumn } from "@/components/ui/data-table"
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalTitle,
  ModalDescription,
} from "@/components/Modal"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { toast } from "sonner"
import { formatDate, formatDateTime } from "@/lib/utils"
import { PageContainer } from "@/components/PageContainer"
import { PageHeader } from "@/components/PageHeader"
import { Chip } from "@/components/Chip"
import { LuUserRound } from "react-icons/lu"
import { ListTooltip } from "@/components/ListTooltip"
import { useCustomDashboards, useStarCustomDashboard, useDeleteCustomDashboard } from "@/data/hooks/api/useDashboards"
import type { CustomDashboardResponse } from "@/data/types/dashboard.types"
import { useAuth } from "@/hooks/useAuth"
import { CustomDashboardUpgradePage } from "./CustomDashboard/UpgradePage"
import dashboardImage1 from "../../assets/dashboard_1.png"
import dashboardImage2 from "../../assets/dashboard_2.png"
import dashboardImage3 from "../../assets/dashboard_3.png"

// Types for custom dashboards
export interface DashboardFilter {
  id: string
  tagName: string
  tagNameLabel: string
  operation: string
  operationLabel: string
  value: string
}

export function CustomDashboardPage() {
  const navigate = useNavigate()
  const { isOnFreePlan } = useAuth()

  const { data, isLoading } = useCustomDashboards(undefined, !isOnFreePlan)
  const starMutation = useStarCustomDashboard()
  const deleteMutation = useDeleteCustomDashboard()

  // Delete dialog state
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [deletingDashboard, setDeletingDashboard] = useState<CustomDashboardResponse | null>(null)

  // Show upgrade page for free plan users
  if (isOnFreePlan) {
    return <CustomDashboardUpgradePage />
  }

  const allDashboards = data?.items || []

  const recommendedFromApi = allDashboards
    .filter(d => d.is_recommended)
    .sort((a, b) => a.display_order - b.display_order)

  const userDashboards = allDashboards
    .filter(d => !d.is_recommended)
    .sort((a, b) => a.display_order - b.display_order)

  const handleToggleStar = async (dashboard: CustomDashboardResponse, e: React.MouseEvent) => {
    e.stopPropagation()
    try {
      await starMutation.mutateAsync({
        dashboardId: dashboard.id,
        star: !dashboard.is_starred
      })
      toast.success(!dashboard.is_starred ? "Dashboard starred" : "Dashboard unstarred")
    } catch {
      toast.error("Failed to update dashboard")
    }
  }

  const handleOpenEditDialog = (dashboard: CustomDashboardResponse, e: React.MouseEvent) => {
    e.stopPropagation()
    // Navigate to dashboard view page where editing now happens
    navigate(`/custom-dashboards/${dashboard.id}`)
  }

  const handleOpenDeleteDialog = (dashboard: CustomDashboardResponse, e: React.MouseEvent) => {
    e.stopPropagation()
    setDeletingDashboard(dashboard)
    setIsDeleteDialogOpen(true)
  }

  const handleDeleteDashboard = async () => {
    if (!deletingDashboard) return

    try {
      await deleteMutation.mutateAsync(deletingDashboard.id)
      setIsDeleteDialogOpen(false)
      setDeletingDashboard(null)
      toast.success("Dashboard deleted successfully")
    } catch {
      toast.error("Failed to delete dashboard")
    }
  }

  const handleRowClick = (dashboard: CustomDashboardResponse) => {
    navigate(`/custom-dashboards/${dashboard.id}`)
  }

  // Column definitions for the dashboards table
  const dashboardsTableColumns: DataTableColumn<CustomDashboardResponse>[] = [
    {
      id: "star",
      header: "",
      flex: 0.3,
      enableSorting: false,
      cell: ({ row }) => {
        const dashboard = row.original
        return (
          <button
            onClick={(e) => handleToggleStar(dashboard, e)}
            className="flex items-center justify-center size-[28px] rounded-[4px] text-[#8A8F98] hover:text-[#F1C40F] hover:bg-[#EDEEF3] transition-colors cursor-pointer"
          >
            {dashboard.is_starred ? (
              <IconStarFilled className="size-4 text-[#F1C40F]" />
            ) : (
              <IconStar className="size-4" />
            )}
          </button>
        )
      },
    },
    {
      id: "name",
      header: "Name",
      accessorKey: "name",
      flex: 1.5,
      enableResizing: true,
      cell: ({ row }) => (
        <span className="font-medium text-[#08090A]">{row.original.name}</span>
      ),
    },
    {
      id: "description",
      header: "Description",
      accessorKey: "description",
      flex: 2,
      enableResizing: true,
      cell: ({ row }) => (
        <span className="text-[#8A8F98] truncate block max-w-[300px]">
          {row.original.description || "—"}
        </span>
      ),
    },
    {
      id: "createdBy",
      header: "Created By",
      flex: 0.8,
      cell: ({ row }) => {
        const creator = row.original.created_by
        if (!creator) return <Chip label="System" icon={<LuUserRound className="size-3" />} />

        return (
          <ListTooltip
            side="top"
            items={[
              {
                id: creator.id,
                label: `${creator.first_name} ${creator.last_name}`,
                sublabel: creator.email,
              },
            ]}
          >
            <div className="inline-flex">
              <Chip
                label={`${creator.first_name} ${creator.last_name}`}
                icon={<LuUserRound className="size-3" />}
              />
            </div>
          </ListTooltip>
        )
      },
    },
    {
      id: "updated_at",
      header: "Last Edited",
      accessorKey: "updated_at",
      flex: 1.2,
      cell: ({ row }) => (
        <span className="text-[#8A8F98]">{formatDateTime(row.original.updated_at)}</span>
      ),
    },
    {
      id: "created_at",
      header: "Created On",
      accessorKey: "created_at",
      flex: 0.8,
      cell: ({ row }) => (
        <span className="text-[#8A8F98]">{formatDate(row.original.created_at)}</span>
      ),
    },
    {
      id: "actions",
      header: "",
      flex: 0.4,
      enableSorting: false,
      cell: ({ row }) => {
        const dashboard = row.original
        return (
          <div className="flex items-center justify-end" onClick={(e) => e.stopPropagation()}>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="flex items-center justify-center size-[28px] rounded-[4px] text-[#8A8F98] hover:text-[#08090A] hover:bg-[#EDEEF3] transition-colors cursor-pointer"
                >
                  <IconDots className="size-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="min-w-[160px] p-[4px] rounded-[6px] border-[#EDEEF3] shadow-lg">
                {!dashboard.is_recommended && (
                  <>
                    <DropdownMenuItem
                      onClick={(e) => handleOpenEditDialog(dashboard, e as unknown as React.MouseEvent)}
                      className="text-[13px] h-[32px] px-[8px] rounded-[4px] text-[#08090A] cursor-pointer"
                    >
                      <IconPencil className="mr-[8px] size-3.5 text-[#8A8F98]" />
                      Edit Dashboard
                    </DropdownMenuItem>
                    <DropdownMenuSeparator className="my-[4px] bg-[#EDEEF3]" />
                    <DropdownMenuItem
                      onClick={(e) => handleOpenDeleteDialog(dashboard, e as unknown as React.MouseEvent)}
                      className="text-[13px] h-[32px] px-[8px] rounded-[4px] text-[#E74C3C] cursor-pointer focus:text-[#E74C3C] focus:bg-[#E74C3C]/5"
                    >
                      <IconTrash className="mr-[8px] size-3.5" />
                      Delete Dashboard
                    </DropdownMenuItem>
                  </>
                )}
                {dashboard.is_recommended && (
                  <DropdownMenuItem
                    onClick={() => navigate(`/custom-dashboards/${dashboard.id}`)}
                    className="text-[13px] h-[32px] px-[8px] rounded-[4px] text-[#08090A] cursor-pointer"
                  >
                    <IconLayoutDashboard className="mr-[8px] size-3.5 text-[#8A8F98]" />
                    View Dashboard
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )
      },
    },
  ]

  return (
    <>
      <PageContainer maxWidth="1200px" className="lg:py-[32px]">
        <PageHeader
          title="Custom Dashboards"
          description="Create and manage personalized dashboards with custom filters to track the metrics that matter most to you."
        />

        {/* Recommended Dashboards Section */}
        {recommendedFromApi.length > 0 && (
          <div className="mt-[32px] mb-[40px]">
            <h2 className="text-[18px] font-medium text-[#08090A] px-[4px] mb-[16px]">Recommended dashboards</h2>
            <div className="overflow-x-auto pb-[8px] -mx-[4px] px-[4px]" style={{ scrollBehavior: 'smooth' }}>
              <div className="flex gap-[16px] min-w-max">
                {recommendedFromApi.map((dashboard, index) => {
                  const title = dashboard.name
                  const description = dashboard.description || ""

                  const truncatedDescription = description.length > 120
                    ? description.substring(0, 120) + "..."
                    : description

                  // Cycle through images: 1, 2, 3, then repeat
                  const imageIndex = (index % 3) + 1
                  const dashboardImages = [dashboardImage1, dashboardImage2, dashboardImage3]
                  const currentImage = dashboardImages[imageIndex - 1]

                  return (
                    <ListTooltip
                      key={dashboard.id}
                      side="bottom"
                      items={[
                        {
                          id: dashboard.id,
                          label: dashboard.description || title,
                        },
                      ]}
                    >
                      <div
                        onClick={() => navigate(`/custom-dashboards/${dashboard.id}`)}
                        className="flex-shrink-0 w-[320px] bg-[#FFFFFF] border border-[#EDEEF3] rounded-[8px] overflow-hidden cursor-pointer hover:border-[#654EDA] hover:shadow-md transition-all group"
                      >
                        {/* Image */}
                        <div className="w-full h-[180px] bg-[#F7F8F8] overflow-hidden">
                          <img
                            src={currentImage}
                            alt={title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                          />
                        </div>

                        {/* Content */}
                        <div className="p-[16px] space-y-[8px]">
                          <h3 className="text-[16px] font-medium text-[#08090A] line-clamp-1">
                            {title}
                          </h3>
                          <p className="text-[13px] text-[#8A8F98] line-clamp-2 leading-[20px]">
                            {truncatedDescription}
                          </p>
                        </div>
                      </div>
                    </ListTooltip>
                  )
                })}
              </div>
            </div>
          </div>
        )}

        <div className="space-y-[13px]">
          <div className="flex items-center justify-between">
            <h2 className="text-[18px] font-medium text-[#08090A] px-[4px]">All Dashboards</h2>
            <Button
              onClick={() => navigate("/custom-dashboards/new")}
              size="sm"
              className="h-[32px] bg-[#654EDA] hover:bg-[#654EDA]/90 text-white font-medium px-[13px] rounded-[6px]"
            >
              <IconPlus className="size-4" />
              Create Dashboard
            </Button>
          </div>
          <DataTable
            data={userDashboards}
            columns={dashboardsTableColumns}
            loading={isLoading}
            getRowId={(row) => row.id}
            emptyMessage="No custom dashboards found"
            emptyIcon={<IconLayoutDashboard className="size-6 text-[#8A8F98]" />}
            onRowClick={handleRowClick}
            pagination={{
              enabled: true,
              mode: "client",
              defaultPageSize: 10,
              pageSizeOptions: [5, 10, 20],
            }}
          />
        </div>
      </PageContainer>

      {/* Delete Dashboard Modal */}
      <Modal open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <ModalContent className="max-w-[480px] p-0 border-[#EDEEF3] shadow-2xl">
          <ModalHeader className="p-[24px] pb-0 space-y-[13px]">
            <ModalTitle className="text-[20px] font-medium text-[#08090A]">Delete Dashboard</ModalTitle>
            <ModalDescription className="text-[13px] leading-[22px] text-[#8A8F98]">
              Are you sure you want to delete the dashboard{" "}
              <span className="font-semibold text-[#08090A]">{deletingDashboard?.name}</span>? This
              action cannot be undone.
            </ModalDescription>
          </ModalHeader>

          <ModalFooter className="justify-end p-[24px] gap-[8px]">
            <Button
              variant="transparent-outline"
              size="sm"
              onClick={() => setIsDeleteDialogOpen(false)}
              disabled={deleteMutation.isPending}
              className="h-[32px] font-medium border-[#EDEEF3]"
            >
              Cancel
            </Button>
            <Button
              variant="default"
              size="sm"
              onClick={handleDeleteDashboard}
              disabled={deleteMutation.isPending}
              className="h-[32px] bg-[#654EDA] hover:bg-[#654EDA]/90 text-white font-medium"
            >
              {deleteMutation.isPending ? "Deleting..." : "Delete Dashboard"}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  )
}
